/**
 * SNES Chat - App Initialization & Routing
 *
 * Handles screen routing between gate and chat screens.
 */

import type { Screen, AppState } from './types';
import { getState, subscribe, isGatePassed } from './state';
import { renderGateScreen } from './gate';
import { renderChatScreen } from './chat';

// ============================================================================
// State
// ============================================================================

let appElement: HTMLElement | null = null;
let currentScreen: Screen = 'gate';

// ============================================================================
// Screen Routing
// ============================================================================

/**
 * Determine which screen to show based on app state
 */
function getActiveScreen(state: AppState): Screen {
  return state.gate.passed ? 'chat' : 'gate';
}

/**
 * Render the current screen
 */
function renderScreen(screen: Screen): void {
  if (!appElement) return;

  // Clear existing content
  appElement.innerHTML = '';

  // Render appropriate screen
  switch (screen) {
    case 'gate':
      renderGateScreen(appElement);
      break;
    case 'chat':
      renderChatScreen(appElement);
      break;
    default:
      console.error('[App] Unknown screen:', screen);
  }

  currentScreen = screen;
}

/**
 * Handle state changes and re-render if needed
 */
function handleStateChange(state: AppState): void {
  const newScreen = getActiveScreen(state);

  if (newScreen !== currentScreen) {
    console.log('[App] Screen transition:', currentScreen, '->', newScreen);
    renderScreen(newScreen);
  }
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize the application
 * T067: Add error context logging
 */
export function initApp(element: HTMLElement): void {
  console.log('[App] Initializing SNES Chat...');
  appElement = element;

  // Set up global error handler for better debugging
  window.onerror = (message, source, lineno, colno, error) => {
    console.error('[App] Uncaught error:', {
      message,
      source,
      line: lineno,
      column: colno,
      error: error?.stack || error,
    });
    return false; // Don't suppress the error
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event) => {
    console.error('[App] Unhandled promise rejection:', event.reason);
  };

  // Subscribe to state changes
  subscribe(handleStateChange);

  // Determine initial screen
  const initialState = getState();
  const initialScreen = getActiveScreen(initialState);

  console.log('[App] Initial screen:', initialScreen);
  console.log('[App] Gate passed:', isGatePassed());

  // Render initial screen
  renderScreen(initialScreen);

  console.log('[App] Initialization complete');
}

/**
 * Get current screen (for testing)
 */
export function getCurrentScreen(): Screen {
  return currentScreen;
}
